import { useState, useRef } from 'react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface UploadedImage {
  filename: string;
  url: string;
  size_kb: number;
}

interface ImageUploaderProps {
  onImageUploaded?: (image: UploadedImage) => void;
}

export default function ImageUploader({ onImageUploaded }: ImageUploaderProps) {
  const [uploads, setUploads] = useState<UploadedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList) => {
    setError('');
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowed.includes(file.type)) {
        setError(`${file.name}: unsupported format. Use PNG, JPG, GIF, or WebP.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: too large. Max 10MB.`);
        continue;
      }

      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/api/onboarding/upload/image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (data.success) {
          const uploaded: UploadedImage = {
            filename: data.filename,
            url: data.image_url,
            size_kb: data.size_kb,
          };
          setUploads(prev => [...prev, uploaded]);
          onImageUploaded?.(uploaded);
        } else {
          setError(data.error || 'Upload failed');
        }
      } catch {
        setError('Upload failed. Please try again.');
      }
    }
    setIsUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
  };

  const removeUpload = (index: number) => {
    setUploads(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all
          ${dragOver
            ? 'border-copper-400 bg-copper-50'
            : 'border-charcoal-200 hover:border-copper-300 hover:bg-cream-50'
          }
          ${isUploading ? 'opacity-60 pointer-events-none' : ''}
        `}
      >
        {isUploading ? (
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-copper-200 border-t-copper-500" />
            <p className="text-sm text-charcoal-400">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <ArrowUpTrayIcon className="h-8 w-8 text-copper-400 mb-2" />
            <p className="text-sm font-medium text-charcoal-600">
              {dragOver ? 'Drop images here' : 'Upload additional images'}
            </p>
            <p className="text-xs text-charcoal-400 mt-1">PNG, JPG, GIF, or WebP — up to 10MB each</p>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Uploaded images */}
      {uploads.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {uploads.map((img, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-charcoal-100">
              <img src={img.url} alt={img.filename} className="w-full h-24 object-cover" />
              <button
                onClick={(e) => { e.stopPropagation(); removeUpload(i); }}
                className="absolute top-1 right-1 p-1 rounded-full bg-charcoal-800/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal-900/60 to-transparent p-1">
                <p className="text-[10px] text-white truncate">{img.size_kb}KB</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
